import type { TableColumnsType } from "@p0security/ui";
import {
  Button,
  Empty,
  Flex,
  Modal,
  Spin,
  Table,
  message,
} from "@p0security/ui";
import { useCallback, useEffect, useMemo } from "react";

import { useMcpClientCredentialRevoke, useMcpClientCredentials } from "./hooks";
import { ErrorAlert } from "./overview/AgenticOverviewContext";

type Credential = { id: string };

/** A credential's revoke action, confirmed in a modal before it runs. */
const RevokeCredentialButton: React.FC<{
  credentialId: string;
  onRevoke: (credentialId: string) => Promise<void>;
}> = ({ credentialId, onRevoke }) => {
  const confirm = useCallback(
    () =>
      Modal.confirm({
        content: `The agent's user must register a new credential to use the '${credentialId}' server again.`,
        okText: "Revoke",
        okType: "danger",
        title: "Revoke credential?",
        // Keeps the modal open, with a busy button, until the revoke settles.
        onOk: () => onRevoke(credentialId),
      }),
    [credentialId, onRevoke]
  );
  return (
    <Button
      aria-label={`Revoke credential for ${credentialId}`}
      danger
      size="small"
      type="link"
      onClick={confirm}
    >
      Revoke
    </Button>
  );
};

/** Lists the upstream OAuth credentials the gateways hold for a federated
 * client, and lets a client manager revoke them. */
export const OauthCredentials: React.FC<{ clientId: string }> = ({
  clientId,
}) => {
  const { error, items, loading, refresh } = useMcpClientCredentials(clientId);
  const revoke = useMcpClientCredentialRevoke();

  // Registration completes in a gateway tab that never reports back, so
  // returning to this tab is when a new credential may have appeared.
  useEffect(() => {
    window.addEventListener("focus", refresh);
    return () => window.removeEventListener("focus", refresh);
  }, [refresh]);

  const revokeCredential = useCallback(
    async (credentialId: string) => {
      if (!(await revoke(clientId, credentialId))) return;
      message.success("Credential revoked");
      refresh();
    },
    [clientId, refresh, revoke]
  );

  const columns = useMemo<TableColumnsType<Credential>>(
    () => [
      { dataIndex: "id", key: "server", title: "MCP server" },
      {
        align: "right",
        key: "actions",
        render: (_, { id }) => (
          <RevokeCredentialButton
            credentialId={id}
            onRevoke={revokeCredential}
          />
        ),
      },
    ],
    [revokeCredential]
  );

  return (
    <Flex gap={12} vertical>
      {error && <ErrorAlert messageText={error} onRetry={refresh} />}
      {/* Only the spinner while loading, rather than overlaying it on stale
          rows or the empty state. */}
      {loading ? (
        <Spin className="flex justify-center" />
      ) : (
        <Table<Credential>
          columns={columns}
          dataSource={items}
          locale={{
            emptyText: (
              <Empty
                description="No OAuth credentials registered."
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
          pagination={{ hideOnSinglePage: true, pageSize: 10 }}
          rowKey="id"
          size="small"
        />
      )}
    </Flex>
  );
};
